import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { countryConfig } from '../../country';
import { Seo } from '../../components/seo';
import { InfoHint } from '../../components/info-hint';
import { LocaleLink } from '../../i18n/locale-link';
import { useLocale, useT } from '../../i18n/locale-context';
import { usePageTitle } from '../../lib/page-title';
import { getPriceReference } from '../../lib/catalog-client';
import { track } from '../../lib/funnel';
import { DEGENERATE_MODE_SHARE_PCT } from '../../lib/metric-thresholds';
import { pricePath, extractIkpu, parentIkpu } from '../../lib/slug';
import { localePath } from '../../i18n/locale-path';
import { formatBudget, formatBudgetCompact } from '../../lib/format';
import { countryNameEn } from '../../lib/country-name-en';

const SITE_ORIGIN = countryConfig.brand.siteUrl;
const KICKER = 'text-[11px] font-bold uppercase tracking-[0.05em] text-ink-muted';

/** Median's position (%) within the p25–p75 range, for the gradient bar marker.
 *  Falls back to the middle when the range is degenerate (p75 === p25). */
function bandFillPct(band: { p25: number; median: number; p75: number }): number {
  if (band.p75 <= band.p25) return 50;
  return Math.min(100, Math.max(0, ((band.median - band.p25) / (band.p75 - band.p25)) * 100));
}

/** Public (#46) per-product price reference: winning-price band, median discount
 *  and competition. Aggregates over the PUBLIC award record only — no per-user
 *  data — so it is indexable SEO content.
 *
 *  A year-over-year median trend was removed 2026-08-19: competitive award
 *  history is really only 2025–2026 per code (only 6 of ~1,350 codes clear the
 *  n≥30 floor in ≥3 distinct years), so the chart was either a noisy 2-bar
 *  non-trend or leaned on the 2019–2023 BidHistory registry — a different, lower
 *  price population whose medians aren't comparable to competitive awards. The
 *  band/discount/participants are the robust signal; the trend added noise and
 *  caveats the data couldn't support. */
/**
 * One metric with its own sample size, or an explicit "not published by the
 * source" when the metric is null. Never renders nothing — see the note at the
 * call site.
 */
function MetricCell({
  label,
  value,
  n,
  basedOn,
  empty,
  note,
}: {
  label: string;
  value: string | null | undefined;
  n: number | undefined;
  basedOn: (n: number) => string;
  empty: string;
  /** Caveat shown under the figure — e.g. the distribution is degenerate. */
  note?: string | null;
}) {
  return (
    <div className="p-5">
      <div className={KICKER}>{label}</div>
      {value == null ? (
        <div className="mt-1 text-[13px] text-ink-muted">{empty}</div>
      ) : (
        <>
          <div className="mt-1 text-[18px] font-bold tabular-nums text-ink">{value}</div>
          {n != null && (
            <div className="mt-0.5 text-[11px] tabular-nums text-ink-muted">{basedOn(n)}</div>
          )}
          {note && <div className="mt-1 text-[11px] leading-snug text-ink-muted">{note}</div>}
        </>
      )}
    </div>
  );
}

export function PriceDetail() {
  // The route param is `{slug}-{ikpu}` (or a bare `{ikpu}`) — recover the code.
  const { ikpu: ikpuParam } = useParams<{ ikpu: string }>();
  const ikpu = ikpuParam ? extractIkpu(ikpuParam) : undefined;
  const navigate = useNavigate();
  const t = useT();
  const { locale } = useLocale();
  usePageTitle(t('prices.title'));
  useEffect(() => {
    if (ikpu) track('prices_viewed', { ikpu });
  }, [ikpu]);

  const { data, isLoading } = useQuery({
    // `locale` is in the key: the product name is localized server-side.
    queryKey: ['price-reference', ikpu, locale],
    queryFn: () => getPriceReference(ikpu!, locale),
    enabled: !!ikpu,
  });

  // The API now always returns `locked: false` (public); the guard stays for the
  // discriminated-union narrowing and any stale cached lock.
  const leafStats = data && !data.locked ? data.stats : null;
  const leafName = data && !data.locked ? data.name : undefined;
  const leafSlug = data && !data.locked ? data.slug : undefined;

  /*
   * Parent-level fallback.
   *
   * The dominant inbound link is «Полная история цен по категории» on the
   * tender page (`base-rates-panel.tsx`). That panel only renders once it has
   * ≥ MIN_SAMPLE comparables — but it finds them by rolling UP the ИКПУ tree
   * (`ikpuOutcomeFilter`), while this page deliberately does NOT roll up
   * (`ikpuSelfAndDescendants`, so a granular product is never blended with its
   * broad section). Both rules are right; the link between them was not.
   *
   * Measured on prod 2026-09-16: 1,929 of the 4,780 live tenders that show the
   * link point at a code with < 8 credible awards, so 40.4% of clicks landed on
   * a single grey sentence that reads as a broken page. Fall back one level and
   * SAY that is what happened — never silently pass the section off as the
   * product's own band.
   */
  const parent = ikpu ? parentIkpu(ikpu) : null;
  const leafEmpty = !!ikpu && !isLoading && leafStats == null;
  const { data: parentData, isLoading: parentLoading } = useQuery({
    queryKey: ['price-reference', parent, locale],
    queryFn: () => getPriceReference(parent!, locale),
    enabled: leafEmpty && parent != null,
  });
  const parentOpen = parentData && !parentData.locked ? parentData : null;
  const fellBack = leafEmpty && parentOpen?.stats != null;

  const stats = leafStats ?? (fellBack ? parentOpen!.stats : null);
  const name = fellBack ? parentOpen!.name : leafName;
  const slug = fellBack ? parentOpen!.slug : leafSlug;
  // Canonical/Dataset must describe the code whose numbers are on screen, not
  // the empty one in the address bar — otherwise the parent's band is indexed
  // under a URL that does not own it.
  const shownIkpu = fellBack ? parent! : ikpu;
  const showSkeleton = isLoading || (leafEmpty && parent != null && parentLoading);

  // Per-page meta description built from this product's OWN numbers. All 386
  // `/prices/:ikpu` pages previously shared `prices.subtitle` — the hub's string —
  // which is the duplicate-metadata signal that keeps thin pages out of the index.
  // Degrades in three steps so that even a product with no band and no sample still
  // gets a description nobody else has (the product name is always distinct).
  const seoDescription = (() => {
    if (!name) return t('prices.subtitle');
    const band = stats?.winningPriceUzs;
    if (band && stats) {
      const money = (v: number) => formatBudgetCompact(String(v), locale) ?? String(v);
      const base = {
        name,
        n: stats.sample,
        median: money(band.median),
        p25: money(band.p25),
        p75: money(band.p75),
      };
      return stats.dateRange
        ? t('prices.seoDesc.bandDated', {
            ...base,
            from: stats.dateRange.from,
            to: stats.dateRange.to,
          })
        : t('prices.seoDesc.band', base);
    }
    if (stats?.sample) {
      return t('prices.seoDesc.sample', { name, n: stats.sample });
    }
    return t('prices.seoDesc.empty', { name });
  })();

  return (
    <>
      <Seo
        // H1/title is the product NAME (SEO), not the raw ИКПУ code; canonical is
        // the slugged `/prices/{slug}-{ikpu}`.
        title={name ? t('prices.seoTitle', { name }) : t('prices.title')}
        description={seoDescription}
        canonical={shownIkpu ? pricePath(slug, shownIkpu) : undefined}
        lang={locale}
        // Dataset markup (a Google-supported type + strong GEO/LLM signal): this
        // is a real aggregate price reference over the public award record. Only
        // emit it once the stats are present in the (pre)rendered HTML — no schema
        // for the empty client shell. temporalCoverage uses the true award window.
        jsonLd={
          stats && shownIkpu
            ? {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'Dataset',
                    name: name ? t('prices.seoTitle', { name }) : t('prices.title'),
                    // Same real, numeric description as the meta tag — this is the copy an
                    // AI answer engine actually quotes, so it must carry the figures too.
                    description: seoDescription,
                    url: `${SITE_ORIGIN}${localePath(pricePath(slug, shownIkpu), locale)}`,
                    isAccessibleForFree: true,
                    inLanguage: locale,
                    creator: {
                      '@type': 'Organization',
                      name: countryConfig.brand.name,
                      url: `${SITE_ORIGIN}/`,
                    },
                    // Name the actual measured variables (was a single duplicate of
                    // the band label) so Dataset Search / AI engines can topic-match.
                    variableMeasured: [
                      t('prices.band'),
                      t('prices.discount'),
                      t('prices.participants'),
                    ],
                    // The award record this band is computed from is country-wide
                    // (it said «Uzbekistan» on kaztender.com too until 2026-10-16).
                    // `Place`, not `Country`: GSC flagged Country as an invalid type
                    // for spatialCoverage on all 4 objects (2026-09-07).
                    spatialCoverage: { '@type': 'Place', name: countryNameEn(countryConfig.code) },
                    // GSC warns on every Dataset without a license; the public
                    // offer is the terms these figures are published under.
                    license: `${SITE_ORIGIN}${localePath('/offer', locale)}`,
                    ...(stats.dateRange
                      ? { temporalCoverage: `${stats.dateRange.from}/${stats.dateRange.to}` }
                      : {}),
                  },
                  {
                    // Home → Prices → this ИКПУ breadcrumb (rendered in the SERP).
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: t('nav.catalog'),
                        item: `${SITE_ORIGIN}${localePath('/', locale)}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: t('nav.prices'),
                        item: `${SITE_ORIGIN}${localePath('/prices', locale)}`,
                      },
                      {
                        '@type': 'ListItem',
                        position: 3,
                        name: name ?? stats.ikpu,
                        item: `${SITE_ORIGIN}${localePath(pricePath(slug, shownIkpu), locale)}`,
                      },
                    ],
                  },
                ],
              }
            : undefined
        }
      />
      <div className="mx-auto max-w-3xl">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="mb-4 text-[13px] font-semibold text-ink-muted hover:text-brand"
        >
          {t('prices.back')}
        </button>

        {showSkeleton ? (
          <div className="h-64 animate-pulse rounded-lg border border-edge bg-bone-surface" />
        ) : stats == null ? (
          /* Absence of data must look different from absence of rendering. Name
             the product (the API returns it even with no stats) and offer the
             one link that goes somewhere. */
          <section className="rounded-lg border border-edge bg-bone-surface p-6 text-center">
            <h1 className="text-[18px] font-bold text-ink">{leafName ?? ikpu}</h1>
            <p className="mt-2 text-[14px] text-ink-muted">{t('prices.insufficient')}</p>
            <LocaleLink
              to="/prices"
              className="mt-3 inline-block text-[13px] font-semibold text-brand hover:underline"
            >
              {t('prices.back')}
            </LocaleLink>
          </section>
        ) : (
          <section className="overflow-hidden rounded-lg border border-edge bg-bone-surface">
            {fellBack && (
              <p className="border-b border-edge-light bg-bone px-5 py-3 text-[12.5px] leading-snug text-ink-muted">
                {t('prices.parentFallback', { code: ikpu!, name: name ?? parent! })}
              </p>
            )}
            <header className="flex items-center gap-1.5 p-5 pb-0">
              <h1 className="text-[18px] font-bold text-ink">{name ?? stats.ikpu}</h1>
              <InfoHint text={t('prices.caveat')} />
            </header>
            {/* The same self-contained, numeric sentence used as the meta
                description, rendered as VISIBLE body prose. AI answer engines
                (Perplexity, AI Overviews) quote crawled body text, not meta tags —
                so the one quotable sentence on the page has to live here, not only
                in <head> (GEO audit, 2026-08-25). */}
            <p className="px-5 pt-2 text-[13px] leading-relaxed text-ink-soft">{seoDescription}</p>

            {stats.winningPriceUzs && (
              <div className="border-t border-edge-light p-5">
                <div className={`mb-2 ${KICKER}`}>{t('prices.band')}</div>
                {/* Gradient range bar with the median marker at its true position
                    within the p25–p75 range, so the three numbers below read as a
                    low → median → high range, not three loose figures. */}
                <div className="relative h-1.5 w-full overflow-visible rounded-pill bg-bone">
                  <div
                    className="h-full rounded-pill bg-gradient-to-r from-brand/40 to-brand"
                    style={{ width: `${bandFillPct(stats.winningPriceUzs)}%` }}
                  />
                  <span
                    aria-hidden
                    className="absolute top-1/2 h-3 w-[3px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-strong"
                    style={{ left: `${bandFillPct(stats.winningPriceUzs)}%` }}
                  />
                </div>
                <div className="mt-2 flex items-baseline justify-between text-[13px] font-semibold tabular-nums text-ink">
                  <span className="flex flex-col">
                    <span className={KICKER}>{t('prices.band.p25')}</span>
                    {formatBudget(String(stats.winningPriceUzs.p25))}
                  </span>
                  <span className="flex flex-col items-center text-brand">
                    <span className={KICKER}>{t('prices.band.median')}</span>
                    {formatBudget(String(stats.winningPriceUzs.median))}
                  </span>
                  <span className="flex flex-col items-end">
                    <span className={KICKER}>{t('prices.band.p75')}</span>
                    {formatBudget(String(stats.winningPriceUzs.p75))}
                  </span>
                </div>
              </div>
            )}

            {/*
              ⚠️ These two are computed over a FRACTION of the headline sample,
              not all of it. Discount needs a starting price, and 162,565 awards
              (uzex-direct + xarid-prequest) carry none — so «скидка 0%» was
              rendering off n=24 while the footer said «Выборка: 7076 закупок».
              Measured 2026-08-01: n=24/7076, n=12/5072, n=13/941. Printing the
              real denominator next to each figure is the difference between a
              statistic and a claim.
            */}
            {/*
              ⚠️ Rendered even when null, as an explicit «нет данных».
              «Топливо дизельное» has 371 awards and ALL of them come from
              xarid-prequest, which publishes no award date, no participant
              count and no starting price. So the page dropped the discount, the
              participants AND the sample footer, and a product with
              371 records rendered as a lone price card — indistinguishable from
              a page that failed to load. Absence of data has to look different
              from absence of rendering.
            */}
            <div className="grid grid-cols-2 divide-x divide-edge-light border-t border-edge-light">
              <MetricCell
                label={t('prices.discount')}
                /* A 0% median where most awards share that exact value is the
                   source recording the announced price as the final one (etender
                   does — measured ~21% overstatement), not a market with no
                   discount. «скидка 0%» read as a finding; it is an absence of
                   data, and is rendered as one (audit 2026-09-07). */
                value={
                  stats.discountPct &&
                  !(
                    stats.discountPct.median === 0 &&
                    stats.discountPct.modeSharePct >= DEGENERATE_MODE_SHARE_PCT
                  )
                    ? `${stats.discountPct.median}%`
                    : null
                }
                n={stats.discountPct?.n}
                basedOn={(n) => t('prices.basedOn', { n })}
                empty={
                  stats.discountPct
                    ? t('prices.discountUnknown', { n: stats.discountPct.n })
                    : t('prices.noSourceData')
                }
                note={
                  stats.discountPct &&
                  stats.discountPct.median !== 0 &&
                  stats.discountPct.modeSharePct >= DEGENERATE_MODE_SHARE_PCT
                    ? t('baseRates.degenerate', { pct: stats.discountPct.modeSharePct })
                    : null
                }
              />
              <MetricCell
                label={t('prices.participants')}
                value={stats.participants && String(stats.participants.median)}
                n={stats.participants?.n}
                basedOn={(n) => t('prices.basedOn', { n })}
                empty={t('prices.noSourceData')}
              />
            </div>

            <div className="border-t border-edge-light p-3 text-center text-[12px] text-ink-muted">
              {stats.dateRange
                ? t('prices.sample', {
                    n: stats.sample,
                    from: stats.dateRange.from,
                    to: stats.dateRange.to,
                  })
                : t('prices.sampleNoDates', { n: stats.sample })}
            </div>
          </section>
        )}
      </div>
    </>
  );
}
